import './scss/main.scss'
import { createApp, h, watch } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { i18n, initializeI18n } from './i18n'
import { createVuestic } from 'vuestic-ui'
import { createGtm } from '@gtm-support/vue-gtm'
import stores from './stores'
import router from './router'
import vuesticGlobalConfig from './services/vuestic-ui/global-config'
import { ApolloClient, createHttpLink, InMemoryCache, split } from '@apollo/client'
import { DefaultApolloClient } from '@vue/apollo-composable'
import { auth0 } from './auth'
import { useAuthStore } from './stores/authStore'
import { User } from '@auth0/auth0-vue'
import persistedState from 'pinia-plugin-persistedstate'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import * as amplitude from '@amplitude/analytics-browser'

const httpLink = createHttpLink({
  uri: import.meta.env.VUE_APP_GRAPHQL_HTTP || 'https://api.wartests.com/graphql',
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GRAPHQL_WSS || 'wss://api.wartests.com/graphql',
  }),
)

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink,
)

// Cache implementation
const cache = new InMemoryCache({
  addTypename: false,
})

const apolloClient = new ApolloClient({
  link,
  cache,
})

const RootComponent = {
  setup() {
    const authStore = useAuthStore()
    amplitude.init('704da308b8447b13ef82299012732ee7', undefined, {
      defaultTracking: true,
    })

    watch(
      () => [auth0.isAuthenticated.value, auth0.user.value],
      ([isAuthenticated, user]) => {
        if (isAuthenticated && user) {
          authStore.logIn(user as User)

          const userId = (user as User).sub
          amplitude.setUserId(userId)
        } else {
          authStore.logOut()
          amplitude.setUserId(undefined)
          amplitude.reset()
        }
      },
      {
        immediate: true,
        deep: true,
      },
    )
  },
  render: () => h(App),
}

const app = createApp(RootComponent)

const pinia = createPinia()
pinia.use(persistedState)
app.use(pinia)

app.use(stores)
app.use(router)
app.use(i18n)
app.use(createVuestic({ config: vuesticGlobalConfig }))
app.use(auth0)

app.provide(DefaultApolloClient, apolloClient)

if (import.meta.env.VITE_APP_GTM_ENABLED) {
  app.use(
    createGtm({
      id: import.meta.env.VITE_APP_GTM_KEY,
      debug: false,
      vueRouter: router,
    }),
  )
}

initializeI18n().then(() => {
  app.mount('#app')
})
